import { useState, useRef, useCallback } from "react";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import elenSinamImg from "@/assets/elen-siman.jpg";
import jucylaineImg from "@/assets/jucylaine-moreira.jpg";
import betaniaImg from "@/assets/betania-garcia.jpg";
import rogeriaImg from "@/assets/rogeria-melo.jpg";

const testimonialImages = [
  "https://static.wixstatic.com/media/df8cc4_33601d74860546358341c4d21bfd2d7c~mv2.jpg/v1/crop/x_132,y_241,w_1235,h_1188/fill/w_256,h_257,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/df8cc4_33601d74860546358341c4d21bfd2d7c~mv2.jpg",
  "https://static.wixstatic.com/media/df8cc4_e72ea84d27724fa183df4eae5db86bfe~mv2.jpg/v1/crop/x_0,y_495,w_2002,h_2010/fill/w_256,h_257,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/C%C3%ADntia%20Ara%C3%BAjo.jpg",
  "https://static.wixstatic.com/media/df8cc4_f277cd45589c4f7bb8e8345ca636e0e8~mv2.jpg/v1/crop/x_3,y_0,w_1434,h_1440/fill/w_256,h_257,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Claudimir%20Celeri.jpg",
  "https://static.wixstatic.com/media/df8cc4_1947d325ced94f13b4e52db321ef8884~mv2.jpg/v1/crop/x_380,y_249,w_631,h_634/fill/w_256,h_257,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Thirza%20Flores.jpg",
  elenSinamImg,
  jucylaineImg,
  betaniaImg,
  rogeriaImg,
];

const DRAG_THRESHOLD = 50;

const TestimonialsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const items = (t("testimonials.items", { returnObjects: true }) as Array<{ quote: string; name: string; role: string }>).map((item, i) => ({
    ...item,
    image: testimonialImages[i],
  }));
  const total = items.length;

  const goNext = useCallback(() => { setActiveIndex((prev) => (prev + 1) % total); setDragOffset(0); }, [total]);
  const goPrev = useCallback(() => { setActiveIndex((prev) => (prev - 1 + total) % total); setDragOffset(0); }, [total]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => { dragStartRef.current = { x: e.clientX, y: e.clientY }; setIsDragging(true); (e.target as HTMLElement).setPointerCapture?.(e.pointerId); }, []);
  const handlePointerMove = useCallback((e: React.PointerEvent) => { if (!dragStartRef.current || !isDragging) return; setDragOffset(e.clientX - dragStartRef.current.x); }, [isDragging]);
  const handlePointerUp = useCallback(() => { if (!isDragging) return; if (Math.abs(dragOffset) > DRAG_THRESHOLD) { if (dragOffset < 0) goNext(); else goPrev(); } else { setDragOffset(0); } setIsDragging(false); dragStartRef.current = null; }, [isDragging, dragOffset, goNext, goPrev]);

  const getCardStyle = (index: number) => {
    let diff = index - activeIndex;
    if (diff > total / 2) diff -= total;
    if (diff < -total / 2) diff += total;
    const absDiff = Math.abs(diff);
    if (absDiff > 2) return { opacity: 0, transform: "scale(0.8) translateY(40px)", zIndex: 0, pointerEvents: "none" as const };
    const scale = 1 - absDiff * 0.06;
    const translateY = absDiff * 16;
    const opacity = 1 - absDiff * 0.25;
    const zIndex = 10 - absDiff;
    const dragX = diff === 0 ? dragOffset : 0;
    const dragRotate = diff === 0 ? dragOffset * 0.04 : 0;
    return {
      transform: `translateX(${dragX}px) translateY(${translateY}px) scale(${scale}) rotate(${dragRotate}deg)`,
      opacity, zIndex,
      pointerEvents: (diff === 0 ? "auto" : "none") as React.CSSProperties["pointerEvents"],
      transition: isDragging && diff === 0 ? "none" : "all 0.5s cubic-bezier(0.25, 0.1, 0.25, 1)",
    };
  };

  return (
    <section id="depoimentos" className="relative py-24 md:py-32">
      <div className="gradient-line mb-24 md:mb-32" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-primary/5 blur-[150px] pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-6 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-14">
          <div>
            <p className="text-sm font-body font-medium text-primary uppercase tracking-wide flex items-center gap-2">
              <span className="led" />
              {t("testimonials.tag")}
            </p>
            <h2 className="mt-3 text-3xl md:text-4xl font-display font-bold tracking-display text-foreground">
              {t("testimonials.title")}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={goPrev} className="w-11 h-11 rounded-full glass flex items-center justify-center text-muted-foreground hover:text-primary hover:shadow-neon-sm hover:border-primary/30 transition-all duration-300" aria-label={t("testimonials.prev")}>
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex gap-1.5">
              {items.map((_, i) => (
                <button key={i} onClick={() => { setActiveIndex(i); setDragOffset(0); }} className={`h-1.5 rounded-full transition-all duration-300 ${i === activeIndex ? "w-8 bg-primary shadow-led" : "w-1.5 bg-muted-foreground/30 hover:bg-muted-foreground/50"}`} aria-label={`${t("testimonials.tag")} ${i + 1}`} />
              ))}
            </div>
            <button onClick={goNext} className="w-11 h-11 rounded-full glass flex items-center justify-center text-muted-foreground hover:text-primary hover:shadow-neon-sm hover:border-primary/30 transition-all duration-300" aria-label={t("testimonials.next")}>
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div ref={containerRef} className="relative w-full select-none h-[580px] sm:h-[420px] md:h-[480px] lg:h-[420px]" onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerLeave={handlePointerUp}>
          {items.map((t_item, index) => (
            <div key={t_item.name} className="absolute inset-0 glass rounded-3xl overflow-hidden cursor-grab active:cursor-grabbing" style={getCardStyle(index)}>
              {index === activeIndex && (
                <div className="absolute inset-0 pointer-events-none" style={{ background: `linear-gradient(105deg, transparent 40%, hsla(199, 76%, 52%, 0.04) 45%, hsla(199, 76%, 52%, 0.08) 50%, hsla(199, 76%, 52%, 0.04) 55%, transparent 60%)`, transform: `translateX(${dragOffset * 0.5}px)` }} />
              )}
              <Quote className="absolute top-6 right-6 md:top-4 md:right-4 lg:top-8 lg:right-8 w-10 h-10 md:w-16 md:h-16 text-primary/8 pointer-events-none" strokeWidth={1} />
              <div className="relative h-full flex flex-col p-6 sm:p-8 md:p-12">
                <div className="flex-1 flex items-center pr-2">
                  <p className="text-sm sm:text-sm md:text-lg font-body text-muted-foreground leading-relaxed italic">&ldquo;{t_item.quote}&rdquo;</p>
                </div>
                <div className="flex items-center gap-4 mt-[10px] pt-[10px] border-t border-primary/10">
                  <img src={t_item.image} alt={t_item.name} className="w-14 h-14 rounded-xl object-cover border-2 border-primary/20 flex-shrink-0 grayscale" loading="lazy" />
                  <div>
                    <p className="text-base font-display font-bold text-foreground">{t_item.name}</p>
                    <p className="text-sm font-body text-primary">{t_item.role}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-2 text-xs font-body text-muted-foreground/40">
            <ChevronLeft className="w-3 h-3" />
            <span>{t("testimonials.dragHint")}</span>
            <ChevronRight className="w-3 h-3" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
